import * as z from "zod/v4";
import { UlidSchema } from "./ulid.ts";
import { SlugSchema } from "./slug.ts";
import { TagsSchema } from "./tag.ts";
import { IsoDateTimeSchema } from "./iso.ts";
import { ResultSchemaOf } from "./result.ts";
import { AttachmentSchema } from "./attachment.ts";
import { nowIsoString } from "../util/date.ts";
import { UnitIntervalSchema } from "./unit-interval.ts";

const emptyArray = <T>(): T[] => [];

export const NoteSchema = z.object({
  slug: SlugSchema.describe(
    "A slug for the note, unique within the project (lowercase word characters and dashes)",
  ),
  project_id: UlidSchema,
  type: SlugSchema.default("note").describe(
    `A type discriminator for the note (lowercase word characters and dashes)`,
  ),
  title: z.string(),
  content: z.string(),
  meta: z.json().describe("JSON metadata"),
  tags: TagsSchema.default(emptyArray),
  source_slugs: z
    .array(SlugSchema)
    .default(emptyArray)
    .describe(
      `If this note was derived from other notes, include their slugs here (provenance)`,
    ),
  source_urls: z
    .array(z.url())
    .default(emptyArray)
    .describe(
      `If this note was derived from any URLs, include them here (provenance)`,
    ),
  confidence: UnitIntervalSchema.default(0.5).describe(
    "Confidence level in the quality or factuality of this note (0..1). Be honest about your confidence and assign an accurate score. It's ok to be unsure. Numbers above 0.8 indicate very high confidence.",
  ),
  author_user_id: z
    .uuid()
    .nullish()
    .describe("The ID of the author who edited this note (if any)"),
  author_agent_id: UlidSchema.nullish().describe(
    "The ID of the agent who edited this note (if any)",
  ),
  attachment_ids: z
    .array(UlidSchema)
    .optional()
    .describe("IDs of attachments associated with this note"),
  created_at: IsoDateTimeSchema.default(nowIsoString),
  updated_at: IsoDateTimeSchema.default(nowIsoString),
});

export type Note = z.infer<typeof NoteSchema>;

export const NoteArraySchema = z.array(NoteSchema);
export type NoteArray = z.infer<typeof NoteArraySchema>;

export const NoteUpsertSchema = NoteSchema.partial({
  title: true,
  content: true,
  meta: true,
  tags: true,
  source_slugs: true,
  source_urls: true,
  confidence: true,
  created_at: true,
  updated_at: true,
});

export type NoteUpsert = z.infer<typeof NoteUpsertSchema>;

export const NoteWithAttachmentsSchema = NoteSchema.extend({
  attachments: z.array(AttachmentSchema).optional(),
});

export type NoteWithAttachments = z.infer<typeof NoteWithAttachmentsSchema>;

export const NoteOptionSchema = NoteSchema.optional();
export type NoteOption = z.infer<typeof NoteOptionSchema>;

export const NoteResultSchema = ResultSchemaOf(NoteSchema, z.string());
export type NoteResult = z.infer<typeof NoteResultSchema>;

export const NoteArrayResultSchema = ResultSchemaOf(
  z.array(NoteSchema),
  z.string(),
);
export type NoteArrayResult = z.infer<typeof NoteArrayResultSchema>;

export const NoteOptionResultSchema = ResultSchemaOf(
  NoteOptionSchema,
  z.string(),
);

export type NoteOptionResult = z.infer<typeof NoteOptionResultSchema>;
